// src/views/admin_sys/StaffPrimSistemiListesi/admin_staff_prim_sistemi_listesi.jsx
import React, { useState, useEffect, useCallback } from 'react'
import { api } from 'services/api'

// ─── HELPERS — birebir aynı ───────────────────────────────────────────────────
const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₺0,00'
    let value = amount
    if (typeof amount === 'string') value = parseFloat(amount.replace(/,/g, ''))
    if (isNaN(value)) value = 0
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value)
}

const parseCurrencyToFloat = (currencyString) => {
    if (!currencyString) return ''
    if (typeof currencyString === 'number') return currencyString
    let clean = currencyString.replace(/[^0-9,.-]+/g, '')
    clean = clean.replace(/,/g, '')
    return parseFloat(clean) || 0
}

const getInitials = (name) => {
    if (!name) return '?'
    const parts = name.split(' ')
    return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase()
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return '-'
    return new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date)
}

// ─── AVATAR COLOR POOL ────────────────────────────────────────────────────────
const AVATAR_COLORS = [
    ['#7c5cfc','#f0eeff'],['#12b886','#e6faf4'],['#f59e0b','#fef3cd'],
    ['#3b82f6','#dbeafe'],['#ec4899','#fce7f3'],['#8b5cf6','#ede9fe'],
    ['#06b6d4','#cffafe'],['#ef4444','#fee2e2'],
]
const avatarColor = (name) => {
    if (!name) return AVATAR_COLORS[0]
    return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length]
}

// ─── SVG ICONS ────────────────────────────────────────────────────────────────
const XIcon = ({ size=16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
)
const SpinnerIcon = ({ size=16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         style={{animation:'prim-spin 0.8s linear infinite',display:'inline-block'}}>
        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeOpacity="0.2"/>
        <path d="M21 12a9 9 0 00-9-9"/>
    </svg>
)
const SearchIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
)
const CalendarIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
)
const ClockIcon = ({ size=13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
    </svg>
)
const MoneyIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
)
const CutIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/>
        <line x1="20" y1="4" x2="8.12" y2="15.88"/>
        <line x1="14.47" y1="14.48" x2="20" y2="20"/>
        <line x1="8.12" y1="8.12" x2="12" y2="12"/>
    </svg>
)
const BasketIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
        <line x1="3" y1="6" x2="21" y2="6"/>
        <path d="M16 10a4 4 0 0 1-8 0"/>
    </svg>
)
const CheckCircleIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
)
const ChartLineIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
)
const CreditCardIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
)
const BookIcon = ({ size=16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    </svg>
)
const ArrowRightIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
)
const FilterIcon = ({ size=11 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
    </svg>
)
const WarnIcon = ({ size=16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
)
const ChevronDownIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="6 9 12 15 18 9"/>
    </svg>
)
const ChevronUpIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="18 15 12 9 6 15"/>
    </svg>
)
const SpeedometerIcon = ({ size=20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2a10 10 0 0 1 7.38 16.75"/>
        <path d="M12 2A10 10 0 0 0 4.62 18.75"/>
        <circle cx="12" cy="12" r="1"/>
        <line x1="12" y1="11" x2="15" y2="8"/>
    </svg>
)

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
// background: transparent — no wrapper background, inherits HorizonUI layout
const GlobalStyles = () => (
    <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

    .prim-page {
      font-family: 'Plus Jakarta Sans', sans-serif;
      background: transparent;   /* ← NO background wrapper */

      --prim-accent:    #7c5cfc;
      --prim-accent-l:  rgba(124,92,252,0.12);
      --prim-accent-h:  #6b4edb;
      --prim-success:   #12b886;
      --prim-success-l: rgba(18,184,134,0.12);
      --prim-error:     #fa5252;
      --prim-error-l:   rgba(250,82,82,0.12);
      --prim-warn:      #f59e0b;
      --prim-warn-l:    rgba(245,158,11,0.12);
      --prim-info:      #3b82f6;
      --prim-info-l:    rgba(59,130,246,0.12);

      /* light */
      --prim-card:      #ffffff;
      --prim-card2:     #f4f2ff;
      --prim-card3:     #f9f8ff;
      --prim-border:    rgba(124,92,252,0.12);
      --prim-border2:   rgba(0,0,0,0.07);
      --prim-text1:     #1a1a2e;
      --prim-text2:     #6b7280;
      --prim-text3:     #9ca3af;
      --prim-shadow:    0 4px 24px rgba(124,92,252,0.07);
      --prim-shadow-lg: 0 12px 40px rgba(124,92,252,0.13);
      --prim-skel1:     rgba(124,92,252,0.08);
      --prim-skel2:     rgba(124,92,252,0.16);
      --prim-track:     rgba(124,92,252,0.1);
    }

    /* ── DARK MODE ── */
    [data-coreui-theme="dark"] .prim-page,
    [data-bs-theme="dark"] .prim-page,
    .dark .prim-page {
      --prim-card:      #1a1928;
      --prim-card2:     #1f1e2e;
      --prim-card3:     #16152a;
      --prim-border:    rgba(124,92,252,0.2);
      --prim-border2:   rgba(255,255,255,0.06);
      --prim-text1:     #f0eeff;
      --prim-text2:     #a09bc0;
      --prim-text3:     #6b6785;
      --prim-shadow:    0 4px 24px rgba(0,0,0,0.35);
      --prim-shadow-lg: 0 12px 40px rgba(0,0,0,0.5);
      --prim-skel1:     rgba(124,92,252,0.08);
      --prim-skel2:     rgba(124,92,252,0.18);
      --prim-track:     rgba(255,255,255,0.08);
    }

    /* ── ANIMATIONS ── */
    @keyframes prim-spin    { to { transform:rotate(360deg); } }
    @keyframes prim-shimmer { 0%{background-position:200% 0}100%{background-position:-200% 0} }
    @keyframes prim-fade-up { from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none} }
    @keyframes prim-modal-in{ from{opacity:0;transform:translateY(-14px) scale(0.97)}to{opacity:1;transform:none} }
    @keyframes prim-progress{ from{width:0}to{width:var(--prim-prog-w)} }
    @keyframes prim-pop     { 0%{transform:scale(0.8);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1} }

    .prim-fade { animation: prim-fade-up 0.35s ease both; }

    /* ── SKELETON ── */
    .prim-skel {
      background: linear-gradient(90deg,var(--prim-skel1) 25%,var(--prim-skel2) 50%,var(--prim-skel1) 75%);
      background-size:200% 100%;
      animation: prim-shimmer 1.5s infinite;
      border-radius:8px;
    }

    /* ── TOP FILTER CARD ── */
    .prim-filter-card {
      background: var(--prim-card);
      border-radius:20px; border:1px solid var(--prim-border);
      box-shadow:var(--prim-shadow);
      padding:20px 24px; margin-bottom:20px;
      display:flex; flex-wrap:wrap;
      align-items:center; justify-content:space-between;
      gap:16px;
    }
    .prim-filter-title { font-size:1.15rem; font-weight:800; color:var(--prim-text1); margin:0; display:flex; align-items:center; gap:10px; }
    .prim-filter-sub   { font-size:0.78rem; color:var(--prim-text3); margin:2px 0 0; }

    /* ── FILTER CHIPS ── */
    .prim-chips-wrap {
      display:flex; gap:4px; padding:4px;
      background:var(--prim-card2); border:1px solid var(--prim-border);
      border-radius:30px;
    }
    .prim-chip {
      padding:6px 16px; border-radius:24px;
      font-family:'Plus Jakarta Sans',sans-serif;
      font-size:0.78rem; font-weight:700;
      background:none; border:none; cursor:pointer;
      color:var(--prim-text3); transition:all 0.2s;
      display:flex; align-items:center; gap:5px; white-space:nowrap;
    }
    .prim-chip:hover { color:var(--prim-text1); background:rgba(124,92,252,0.06); }
    .prim-chip.active {
      background:var(--prim-accent); color:#fff;
      box-shadow:0 4px 12px rgba(124,92,252,0.35);
    }

    /* ── DATE RANGE ── */
    .prim-date-range {
      display:flex; align-items:center; gap:8px;
      background:var(--prim-card2); border:1.5px solid var(--prim-border);
      border-radius:12px; padding:7px 12px;
      flex-wrap:wrap;
    }
    .prim-date-input {
      background:transparent; border:none; outline:none;
      color:var(--prim-text1); font-family:'Plus Jakarta Sans',sans-serif;
      font-size:0.85rem; font-weight:700; min-width:120px;
      color-scheme: light dark;
    }
    .prim-date-sep { color:var(--prim-text3); font-size:0.75rem; }
    .prim-search-btn {
      width:30px; height:30px; border-radius:8px;
      background:var(--prim-accent); border:none;
      color:#fff; cursor:pointer; display:flex; align-items:center; justify-content:center;
      transition:all 0.18s; flex-shrink:0;
    }
    .prim-search-btn:hover { background:var(--prim-accent-h); }
    .prim-search-btn:disabled { opacity:0.5; cursor:not-allowed; }

    .prim-help-btn {
      width:36px; height:36px; border-radius:10px;
      background:var(--prim-info-l); border:1.5px solid var(--prim-border);
      color:var(--prim-info); cursor:pointer;
      display:flex; align-items:center; justify-content:center;
      transition:all 0.18s;
    }
    .prim-help-btn:hover { background:var(--prim-info); color:#fff; }

    /* ── SUMMARY GRID ── */
    .prim-summary-grid {
      display:grid; gap:12px; margin-bottom:20px;
      grid-template-columns:1fr;
    }
    @media(min-width:640px) { .prim-summary-grid { grid-template-columns:repeat(2,1fr); } }
    @media(min-width:900px) { .prim-summary-grid { grid-template-columns:repeat(3,1fr); } }

    /* ── SUMMARY HERO (total revenue) ── */
    .prim-hero-card {
      background:linear-gradient(135deg,var(--prim-accent),#a855f7);
      border-radius:20px; padding:24px;
      position:relative; overflow:hidden;
      box-shadow:0 12px 40px rgba(124,92,252,0.3);
      animation: prim-pop 0.4s ease both;
    }
    .prim-hero-card::before {
      content:''; position:absolute; top:-50px; right:-50px;
      width:180px; height:180px; border-radius:50%;
      background:rgba(255,255,255,0.06);
    }
    .prim-hero-label { font-size:0.65rem; font-weight:800; letter-spacing:0.8px; text-transform:uppercase; color:rgba(255,255,255,0.7); margin:0 0 6px; }
    .prim-hero-value { font-size:2rem; font-weight:900; color:#fff; margin:0; line-height:1; position:relative; z-index:1; }
    .prim-hero-sub   { font-size:0.72rem; color:rgba(255,255,255,0.55); margin:6px 0 0; display:flex; align-items:center; gap:4px; position:relative; z-index:1; }

    /* ── REVENUE BREAKDOWN CARD ── */
    .prim-rev-card {
      background:var(--prim-card); border-radius:20px;
      border:1px solid var(--prim-border); box-shadow:var(--prim-shadow);
      padding:20px; animation: prim-pop 0.4s 0.06s ease both;
    }
    .prim-rev-row { display:flex; justify-content:space-between; align-items:center; margin-bottom:10px; }
    .prim-rev-label { font-size:0.82rem; font-weight:700; color:var(--prim-text1); display:flex; align-items:center; gap:6px; }
    .prim-rev-value { font-size:0.9rem; font-weight:800; color:var(--prim-text1); }

    /* ── PROGRESS BAR ── */
    .prim-progress-track {
      height:8px; background:var(--prim-track); border-radius:8px; overflow:hidden; margin-bottom:16px;
    }
    .prim-progress-fill {
      height:100%; border-radius:8px; transition:width 1s cubic-bezier(0.4,0,0.2,1);
    }
    .prim-progress-fill.striped {
      background-size:16px 16px;
      animation:prim-shimmer 1s linear infinite;
    }

    /* ── OPERATION CARD ── */
    .prim-ops-card {
      background:var(--prim-card); border-radius:20px;
      border:1px solid var(--prim-border); box-shadow:var(--prim-shadow);
      padding:20px; display:flex; flex-direction:column; justify-content:center;
      animation: prim-pop 0.4s 0.1s ease both;
    }
    .prim-ops-label { font-size:0.65rem; font-weight:800; letter-spacing:0.6px; text-transform:uppercase; color:var(--prim-text3); margin:0 0 12px; text-align:center; }
    .prim-ops-grid  { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
    .prim-ops-item  { text-align:center; padding:12px 8px; background:var(--prim-card2); border-radius:12px; border:1px solid var(--prim-border2); }
    .prim-ops-num   { font-size:1.6rem; font-weight:900; color:var(--prim-text1); margin:0; line-height:1; }
    .prim-ops-name  { font-size:0.68rem; color:var(--prim-text3); margin:4px 0 0; font-weight:600; }

    /* ── SECTION DIVIDER ── */
    .prim-section-divider {
      display:flex; align-items:center; gap:14px; margin:28px 0 16px;
    }
    .prim-section-label { font-size:1rem; font-weight:800; color:var(--prim-text1); white-space:nowrap; }
    .prim-section-line  { flex:1; height:1px; background:var(--prim-border2); }

    /* ── STAFF GRID ── */
    .prim-staff-grid {
      display:grid; gap:14px;
      grid-template-columns:1fr;
    }
    @media(min-width:640px) { .prim-staff-grid { grid-template-columns:repeat(2,1fr); } }
    @media(min-width:1100px){ .prim-staff-grid { grid-template-columns:repeat(3,1fr); } }

    /* ── STAFF CARD ── */
    .prim-staff-card {
      background:var(--prim-card); border-radius:20px;
      border:2px solid var(--prim-border); box-shadow:var(--prim-shadow);
      overflow:hidden; display:flex; flex-direction:column;
      transition:all 0.25s ease;
      animation: prim-fade-up 0.3s ease both;
    }
    .prim-staff-card:hover {
      border-color:var(--prim-accent);
      transform:translateY(-5px);
      box-shadow:0 12px 36px rgba(124,92,252,0.18);
    }

    /* ── STAFF CARD HEADER ── */
    .prim-staff-header {
      padding:18px 20px; display:flex; align-items:center; gap:14px;
      background:linear-gradient(135deg,var(--prim-accent-l),transparent);
      border-bottom:1px solid var(--prim-border2);
    }
    .prim-avatar {
      width:56px; height:56px; border-radius:50%; flex-shrink:0;
      display:flex; align-items:center; justify-content:center;
      font-size:1.1rem; font-weight:800; position:relative;
      border:2.5px solid var(--prim-accent);
    }
    .prim-avatar img { width:100%; height:100%; object-fit:cover; border-radius:50%; }
    .prim-avatar-online {
      position:absolute; bottom:1px; right:1px;
      width:12px; height:12px; border-radius:50%;
      background:var(--prim-success); border:2px solid var(--prim-card);
    }
    .prim-staff-name  { font-size:0.95rem; font-weight:800; color:var(--prim-text1); margin:0 0 4px; }
    .prim-model-badge {
      display:inline-flex; align-items:center; gap:4px;
      font-size:0.65rem; font-weight:700; padding:2px 8px; border-radius:20px;
      background:var(--prim-card2); border:1px solid var(--prim-border);
      color:var(--prim-text2);
    }

    /* ── STAFF CARD BODY ── */
    .prim-staff-body { padding:20px; flex:1; display:flex; flex-direction:column; }

    /* ── COMMISSION MODEL SECTIONS ── */
    .prim-model-section { margin-bottom:18px; }
    .prim-model-label {
      font-size:0.65rem; font-weight:800; letter-spacing:0.6px;
      text-transform:uppercase; margin-bottom:8px; display:block;
    }

    /* Threshold progress */
    .prim-threshold-header { display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:8px; }
    .prim-threshold-current { font-size:0.95rem; font-weight:800; color:var(--prim-text1); }
    .prim-threshold-target  { font-size:0.75rem; color:var(--prim-text3); }
    .prim-breakdown-box {
      background:var(--prim-card2); border:1px solid var(--prim-border2);
      border-radius:10px; padding:10px 12px; margin-top:8px;
    }
    .prim-breakdown-row { display:flex; justify-content:space-between; align-items:center; font-size:0.78rem; margin-bottom:4px; }
    .prim-breakdown-row:last-child { margin-bottom:0; }
    .prim-breakdown-label { color:var(--prim-text3); display:flex; align-items:center; gap:4px; }
    .prim-breakdown-val   { font-weight:700; color:var(--prim-text1); }

    /* Tiered info box */
    .prim-tiered-box {
      background:var(--prim-info-l); border:1px solid rgba(59,130,246,0.2);
      border-radius:12px; padding:12px 16px; text-align:center;
    }
    .prim-tiered-detail { font-size:0.85rem; font-weight:700; color:var(--prim-text1); margin:4px 0 0; }

    /* Progress legend */
    .prim-legend { display:flex; justify-content:space-between; font-size:0.72rem; color:var(--prim-text3); margin-top:5px; }

    /* ── COMMISSION EARNED BOX ── */
    .prim-earned-box {
      background:linear-gradient(135deg,var(--prim-success),#059669);
      border-radius:16px; padding:16px;
      position:relative; overflow:hidden; margin-bottom:16px;
    }
    .prim-earned-box::before {
      content:''; position:absolute; top:-20px; right:-20px;
      width:80px; height:80px; border-radius:50%; background:rgba(255,255,255,0.08);
    }
    .prim-earned-label { font-size:0.65rem; font-weight:800; letter-spacing:0.6px; text-transform:uppercase; color:rgba(255,255,255,0.75); margin:0 0 4px; }
    .prim-earned-value { font-size:1.6rem; font-weight:900; color:#fff; margin:0; position:relative; z-index:1; }
    .prim-earned-footer { margin-top:8px; padding-top:8px; border-top:1px solid rgba(255,255,255,0.15); font-size:0.75rem; color:rgba(255,255,255,0.7); display:flex; justify-content:space-between; }

    /* ── FINANCIAL HISTORY ── */
    .prim-fin-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-bottom:18px; }
    .prim-fin-item {
      background:var(--prim-card2); border:1px solid var(--prim-border2);
      border-radius:12px; padding:10px 12px;
    }
    .prim-fin-icon-label { display:flex; align-items:center; gap:5px; font-size:0.7rem; font-weight:700; margin-bottom:5px; }
    .prim-fin-amount { font-size:0.88rem; font-weight:800; color:var(--prim-text1); margin:0; }
    .prim-fin-date   { font-size:0.62rem; color:var(--prim-text3); margin:2px 0 0; }

    /* ── ACTION BUTTONS ── */
    .prim-action-row { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:auto; }
    .prim-action-btn {
      padding:10px 12px; border-radius:12px;
      font-family:'Plus Jakarta Sans',sans-serif;
      font-size:0.78rem; font-weight:700;
      cursor:pointer; border:none; transition:all 0.18s;
      display:flex; align-items:center; justify-content:center; gap:6px;
    }
    .prim-btn-salary {
      background:var(--prim-card2); color:var(--prim-text2);
      border:1.5px solid var(--prim-border);
    }
    .prim-btn-salary:hover { border-color:var(--prim-info); color:var(--prim-info); background:var(--prim-info-l); }
    .prim-btn-commission {
      background:linear-gradient(135deg,var(--prim-success),#059669);
      color:#fff; border:none;
    }
    .prim-btn-commission:hover { box-shadow:0 6px 16px rgba(18,184,134,0.35); transform:translateY(-1px); }
    .prim-action-btn:disabled { opacity:0.5; cursor:not-allowed; transform:none !important; }

    /* ── ALERT ── */
    .prim-alert {
      display:flex; align-items:center; gap:10px; padding:12px 16px;
      border-radius:14px; font-size:0.85rem; font-weight:600;
      margin-bottom:16px; animation:prim-fade-up 0.25s ease;
      box-shadow:var(--prim-shadow);
    }
    .prim-alert-danger { background:var(--prim-error-l); color:var(--prim-error); border:1px solid rgba(250,82,82,0.25); }
    .prim-alert-close  { background:none; border:none; cursor:pointer; color:inherit; opacity:0.6; padding:0; margin-left:auto; }
    .prim-alert-close:hover { opacity:1; }

    /* ── FULL LOADER ── */
    .prim-loader {
      display:flex; flex-direction:column; align-items:center; justify-content:center;
      padding:64px 0; gap:14px;
    }
    .prim-spinner {
      width:48px; height:48px; border-radius:50%;
      border:3px solid var(--prim-accent-l); border-top-color:var(--prim-accent);
      animation:prim-spin 0.8s linear infinite;
    }
    .prim-loader-text { font-size:0.85rem; font-weight:600; color:var(--prim-text3); margin:0; }

    /* ── MODAL OVERLAY ── */
    .prim-modal-overlay {
      position:fixed; inset:0;
      background:rgba(0,0,0,0.55); backdrop-filter:blur(4px);
      z-index:1050; display:flex; align-items:center; justify-content:center; padding:16px;
    }
    .prim-modal {
      background:var(--prim-card); border-radius:20px;
      border:1px solid var(--prim-border); box-shadow:var(--prim-shadow-lg);
      width:100%; max-width:480px; max-height:90vh;
      display:flex; flex-direction:column;
      animation:prim-modal-in 0.2s ease both;
    }
    .prim-modal-lg { max-width:640px; }
    .prim-modal-header {
      padding:18px 22px; border-bottom:1px solid var(--prim-border2);
      display:flex; align-items:center; justify-content:space-between; flex-shrink:0;
    }
    .prim-modal-title { font-size:1rem; font-weight:800; color:var(--prim-text1); }
    .prim-modal-close {
      width:30px; height:30px; border-radius:8px;
      background:var(--prim-card2); border:none; cursor:pointer;
      color:var(--prim-text3); display:flex; align-items:center; justify-content:center;
      transition:all 0.15s;
    }
    .prim-modal-close:hover { background:var(--prim-error-l); color:var(--prim-error); }
    .prim-modal-body   { padding:22px; overflow-y:auto; flex:1; }
    .prim-modal-footer { padding:14px 22px; border-top:1px solid var(--prim-border2); display:flex; justify-content:flex-end; gap:10px; flex-shrink:0; }

    /* ── MODAL FORM ── */
    .prim-form-label { display:block; margin-bottom:7px; font-size:0.7rem; font-weight:700; letter-spacing:0.6px; color:var(--prim-text3); text-transform:uppercase; }
    .prim-input-group { display:flex; }
    .prim-input-prefix {
      padding:11px 13px; background:var(--prim-card2);
      border:1.5px solid var(--prim-border); border-right:none;
      border-radius:12px 0 0 12px; color:var(--prim-text3);
      font-size:0.9rem; font-weight:700; display:flex; align-items:center;
    }
    .prim-input {
      flex:1; padding:11px 13px;
      background:var(--prim-card2); border:1.5px solid var(--prim-border); border-radius:0 12px 12px 0;
      color:var(--prim-text1); font-family:'Plus Jakarta Sans',sans-serif;
      font-size:1.1rem; font-weight:800; text-align:center;
      outline:none; transition:border-color 0.2s;
    }
    .prim-input:focus { border-color:var(--prim-accent); }
    .prim-input::placeholder { color:var(--prim-text3); font-size:0.9rem; font-weight:400; }
    .prim-textarea {
      width:100%; padding:11px 13px;
      background:var(--prim-card2); border:1.5px solid var(--prim-border);
      border-radius:12px; color:var(--prim-text1);
      font-family:'Plus Jakarta Sans',sans-serif; font-size:0.85rem;
      resize:none; outline:none; box-sizing:border-box; transition:border-color 0.2s;
    }
    .prim-textarea:focus { border-color:var(--prim-accent); }
    .prim-textarea::placeholder { color:var(--prim-text3); }
    .prim-hint-success { font-size:0.72rem; color:var(--prim-success); display:flex; align-items:center; gap:4px; margin-top:5px; }
    .prim-modal-staff-name { font-size:1rem; font-weight:800; color:var(--prim-text1); margin:0 0 2px; text-align:center; }
    .prim-modal-staff-sub  { font-size:0.75rem; color:var(--prim-text3); text-align:center; margin:0 0 18px; }

    /* ── BUTTON ── */
    .prim-btn {
      display:inline-flex; align-items:center; justify-content:center; gap:7px;
      padding:10px 20px; border-radius:12px;
      font-family:'Plus Jakarta Sans',sans-serif; font-size:0.85rem; font-weight:700;
      cursor:pointer; transition:all 0.18s; border:none; white-space:nowrap;
    }
    .prim-btn-primary { background:var(--prim-accent); color:#fff; }
    .prim-btn-primary:hover:not(:disabled){ background:var(--prim-accent-h); box-shadow:0 4px 14px rgba(124,92,252,0.3); }
    .prim-btn-success { background:var(--prim-success); color:#fff; }
    .prim-btn-success:hover:not(:disabled){ background:#0ca678; box-shadow:0 4px 14px rgba(18,184,134,0.3); }
    .prim-btn-info    { background:var(--prim-info);    color:#fff; }
    .prim-btn-info:hover:not(:disabled)   { background:#2563eb; }
    .prim-btn-ghost   { background:var(--prim-card2); color:var(--prim-text2); border:1.5px solid var(--prim-border); }
    .prim-btn-ghost:hover:not(:disabled)  { border-color:var(--prim-accent); color:var(--prim-accent); }
    .prim-btn:disabled{ opacity:0.5; cursor:not-allowed; }

    /* ── ACCORDION (FAQ) ── */
    .prim-accordion { display:flex; flex-direction:column; gap:8px; }
    .prim-acc-item {
      background:var(--prim-card2); border:1.5px solid var(--prim-border);
      border-radius:14px; overflow:hidden;
    }
    .prim-acc-header {
      width:100%; display:flex; align-items:center; justify-content:space-between;
      padding:14px 18px; background:none; border:none; cursor:pointer;
      font-family:'Plus Jakarta Sans',sans-serif; font-size:0.88rem; font-weight:700;
      color:var(--prim-text1); text-align:left; transition:background 0.15s;
    }
    .prim-acc-header:hover { background:rgba(124,92,252,0.04); }
    .prim-acc-body {
      padding:0 18px 14px; font-size:0.82rem; color:var(--prim-text2); line-height:1.6;
      animation:prim-fade-up 0.2s ease;
    }
  `}</style>
)

// ─── PROGRESS BAR ─────────────────────────────────────────────────────────────
const ProgressBar = ({ value, color = 'var(--prim-accent)', striped = false, height = 8 }) => {
    const pct = Math.min(Math.max(value || 0, 0), 100)
    return (
        <div className="prim-progress-track" style={{ height }}>
            <div
                className={`prim-progress-fill${striped ? ' striped' : ''}`}
                style={{
                    width: `${pct}%`,
                    background: striped
                        ? `repeating-linear-gradient(45deg,${color} 0,${color} 8px,rgba(255,255,255,0.15) 8px,rgba(255,255,255,0.15) 16px)`
                        : color,
                    backgroundSize: striped ? '16px 16px' : undefined,
                }}
            />
        </div>
    )
}

// ─── ACCORDION ITEM ──────────────────────────────────────────────────────────
const AccordionItem = ({ title, children, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen)
    return (
        <div className="prim-acc-item">
            <button className="prim-acc-header" onClick={() => setOpen(p => !p)}>
                <span>{title}</span>
                {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </button>
            {open && <div className="prim-acc-body">{children}</div>}
        </div>
    )
}

// ─── MODAL ────────────────────────────────────────────────────────────────────
const Modal = ({ visible, onClose, title, children, footer, size = '' }) => {
    useEffect(() => {
        const fn = (e) => e.key === 'Escape' && onClose()
        if (visible) document.addEventListener('keydown', fn)
        return () => document.removeEventListener('keydown', fn)
    }, [visible, onClose])
    if (!visible) return null
    return (
        <div className="prim-modal-overlay" onClick={(e) => e.target.classList.contains('prim-modal-overlay') && onClose()}>
            <div className={`prim-modal ${size}`}>
                <div className="prim-modal-header">
                    <span className="prim-modal-title">{title}</span>
                    <button className="prim-modal-close" onClick={onClose}><XIcon size={14} /></button>
                </div>
                <div className="prim-modal-body">{children}</div>
                {footer && <div className="prim-modal-footer">{footer}</div>}
            </div>
        </div>
    )
}

// ─── renderCommissionDetails — birebir iş mantığı aynı ───────────────────────
const CommissionDetails = ({ staff }) => {
    const model    = staff.commission_model
    const metrics  = staff.metrics
    const settings = staff.model_settings || {}

    // 1. EŞİKLİ SİSTEM
    if (model === 'threshold_complex') {
        const threshold = parseFloat(settings.threshold || 0)
        const current   = parseFloat(metrics.total_revenue || 0)
        const percent   = threshold > 0 ? Math.min((current / threshold) * 100, 100) : 0
        const isPassed  = current >= threshold

        return (
            <div className="prim-model-section prim-fade">
                <span className="prim-model-label" style={{ color: 'var(--prim-warn)' }}>Hedef Kota</span>
                <div className="prim-threshold-header">
                    <span />
                    <div style={{ textAlign: 'right' }}>
            <span className={`prim-threshold-current`} style={{ color: isPassed ? 'var(--prim-success)' : 'var(--prim-text1)' }}>
              {formatCurrency(current)}
            </span>
                        <span className="prim-threshold-target"> / {formatCurrency(threshold)}</span>
                    </div>
                </div>
                <ProgressBar
                    value={percent}
                    color={isPassed ? 'var(--prim-success)' : 'var(--prim-warn)'}
                    striped={!isPassed}
                    height={10}
                />
                <div className="prim-breakdown-box" style={{ marginTop: 8 }}>
                    <div className="prim-breakdown-row">
            <span className="prim-breakdown-label" style={{ color: 'var(--prim-success)' }}>
              <MoneyIcon size={11} /> Nakit Ciro:
            </span>
                        <span className="prim-breakdown-val">
              {formatCurrency(
                  (metrics.commission_breakdown?.service_cash || 0) +
                  (metrics.commission_breakdown?.product_cash || 0)
              )}
            </span>
                    </div>
                    <div className="prim-breakdown-row">
            <span className="prim-breakdown-label" style={{ color: 'var(--prim-info)' }}>
              <CreditCardIcon size={11} /> Kart Ciro:
            </span>
                        <span className="prim-breakdown-val">
              {formatCurrency(
                  (metrics.commission_breakdown?.service_card || 0) +
                  (metrics.commission_breakdown?.product_card || 0)
              )}
            </span>
                    </div>
                </div>
            </div>
        )
    }

    // 2. KADEMELİ SİSTEM
    if (model === 'tiered') {
        return (
            <div className="prim-model-section prim-fade">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span className="prim-model-label" style={{ color: 'var(--prim-info)', margin: 0 }}>Ciro Performansı</span>
                    <span style={{ fontWeight: 800, color: 'var(--prim-text1)', fontSize: '0.9rem' }}>{formatCurrency(metrics.total_revenue)}</span>
                </div>
                <div className="prim-tiered-box">
                    <ChartLineIcon size={20} style={{ color: 'var(--prim-info)', marginBottom: 6 }} />
                    <p style={{ fontSize: '0.72rem', color: 'var(--prim-text3)', margin: '0 0 4px' }}>Durum</p>
                    <p className="prim-tiered-detail">{staff.calculation_details || 'Hesaplanıyor...'}</p>
                </div>
            </div>
        )
    }

    // 3. SABİT ORAN (DEFAULT)
    const serviceRev = metrics.service_revenue || 0
    const productRev = metrics.product_revenue || 0
    const totalRev   = metrics.total_revenue || 1
    return (
        <div className="prim-model-section prim-fade">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span className="prim-model-label" style={{ margin: 0 }}>Ciro Dağılımı</span>
                <span style={{ fontWeight: 800, color: 'var(--prim-text1)', fontSize: '1rem' }}>{formatCurrency(totalRev)}</span>
            </div>
            <ProgressBar value={(serviceRev / totalRev) * 100} color="var(--prim-info)" height={8} />
            <div style={{ height: 3 }} />
            <ProgressBar value={(productRev / totalRev) * 100} color="var(--prim-warn)" height={8} />
            <div className="prim-legend">
                <span><span style={{ color: 'var(--prim-info)' }}>●</span> Hizmet: {formatCurrency(serviceRev)}</span>
                <span><span style={{ color: 'var(--prim-warn)' }}>●</span> Ürün: {formatCurrency(productRev)}</span>
            </div>
        </div>
    )
}

// ─── STAFF CARD ───────────────────────────────────────────────────────────────
const StaffCard = ({ staff, onPayment, animDelay }) => {
    const [bg, fg] = avatarColor(staff.staff_name)

    return (
        <div className="prim-staff-card" style={{ animationDelay: `${animDelay}s` }}>
            {/* HEADER */}
            <div className="prim-staff-header">
                <div className="prim-avatar" style={{ background: bg, color: fg }}>
                    {staff.staff_image
                        ? <img src={staff.staff_image} alt={staff.staff_name} />
                        : getInitials(staff.staff_name)
                    }
                    <div className="prim-avatar-online" />
                </div>
                <div style={{ flex: 1 }}>
                    <p className="prim-staff-name">{staff.staff_name}</p>
                    <span className="prim-model-badge">
            <FilterIcon />{staff.commission_model_label || 'Standart'}
          </span>
                </div>
            </div>

            {/* BODY */}
            <div className="prim-staff-body">
                {/* Dinamik ciro & performans gösterimi */}
                <CommissionDetails staff={staff} />

                {/* HAK EDİŞ */}
                <div className="prim-earned-box">
                    <p className="prim-earned-label">Toplam Hakediş</p>
                    <p className="prim-earned-value">{formatCurrency(staff.metrics.commission_earned)}</p>
                    {staff.commission_model === 'flat_rate' && (
                        <div className="prim-earned-footer">
                            <span>Hizmetten: <strong>{staff.metrics.commission_breakdown?.from_services} ₺</strong></span>
                            <span>Üründen: <strong>{staff.metrics.commission_breakdown?.from_products} ₺</strong></span>
                        </div>
                    )}
                    {staff.commission_model !== 'flat_rate' && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.15)', fontSize: '0.75rem', color: 'rgba(255,255,255,0.65)' }}>
                            {staff.calculation_details}
                        </div>
                    )}
                </div>

                {/* FİNANSAL GEÇMİŞ */}
                <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--prim-text3)', marginBottom: 8 }}>
                        Son Ödeme Geçmişi
                    </p>
                    <div className="prim-fin-grid">
                        <div className="prim-fin-item">
                            <div className="prim-fin-icon-label" style={{ color: 'var(--prim-info)' }}>
                                <MoneyIcon size={12} /> Maaş
                            </div>
                            <p className="prim-fin-amount">{formatCurrency(staff.financial_status.last_salary.amount)}</p>
                            <p className="prim-fin-date">{formatDate(staff.financial_status.last_salary.date)}</p>
                        </div>
                        <div className="prim-fin-item">
                            <div className="prim-fin-icon-label" style={{ color: 'var(--prim-warn)' }}>
                                <CreditCardIcon size={12} /> Prim
                            </div>
                            <p className="prim-fin-amount">{formatCurrency(staff.financial_status.last_commission.amount)}</p>
                            <p className="prim-fin-date">{formatDate(staff.financial_status.last_commission.date)}</p>
                        </div>
                    </div>
                </div>

                {/* AKSİYON BUTONLARI */}
                <div className="prim-action-row">
                    <button className="prim-action-btn prim-btn-salary" onClick={() => onPayment(staff, 'salary')}>
                        <MoneyIcon size={13} /> Maaş
                    </button>
                    <button className="prim-action-btn prim-btn-commission" onClick={() => onPayment(staff, 'commission')}>
                        <CheckCircleIcon size={13} /> Prim Öde
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─── SKELETON ─────────────────────────────────────────────────────────────────
const StaffCardSkeleton = () => (
    <div style={{ background: 'var(--prim-card)', borderRadius: 20, border: '2px solid var(--prim-border)', overflow: 'hidden', padding: 0 }}>
        <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14, borderBottom: '1px solid var(--prim-border2)' }}>
            <div className="prim-skel" style={{ width: 56, height: 56, borderRadius: '50%', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
                <div className="prim-skel" style={{ width: '70%', height: 16, marginBottom: 8 }} />
                <div className="prim-skel" style={{ width: '45%', height: 12 }} />
            </div>
        </div>
        <div style={{ padding: 20 }}>
            <div className="prim-skel" style={{ width: '100%', height: 14, marginBottom: 10 }} />
            <div className="prim-skel" style={{ width: '100%', height: 8, marginBottom: 16 }} />
            <div className="prim-skel" style={{ width: '100%', height: 72, borderRadius: 16, marginBottom: 14 }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                <div className="prim-skel" style={{ height: 64, borderRadius: 12 }} />
                <div className="prim-skel" style={{ height: 64, borderRadius: 12 }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div className="prim-skel" style={{ height: 40, borderRadius: 12 }} />
                <div className="prim-skel" style={{ height: 40, borderRadius: 12 }} />
            </div>
        </div>
    </div>
)

const SummarySkeleton = () => (
    <div style={{ display: 'grid', gap: 12, marginBottom: 20, gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))' }}>
        {[0, 1, 2].map(i => (
            <div key={i} style={{ background: 'var(--prim-card)', borderRadius: 20, border: '1px solid var(--prim-border)', padding: 24 }}>
                <div className="prim-skel" style={{ width: '50%', height: 12, marginBottom: 12 }} />
                <div className="prim-skel" style={{ width: '75%', height: 28, marginBottom: 10 }} />
                <div className="prim-skel" style={{ width: '40%', height: 10 }} />
            </div>
        ))}
    </div>
)

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const AdminStaffPrimSistemiListesi = () => {
    // ── STATE — birebir aynı ──────────────────────────────────────────────────
    const [loading,            setLoading]            = useState(true)
    const [reportData,         setReportData]         = useState(null)
    const [error,              setError]              = useState(null)
    const [activeFilter,       setActiveFilter]       = useState('custom')

    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0])
    const [endDate,   setEndDate]   = useState(new Date().toISOString().split('T')[0])

    const [paymentModal, setPaymentModal] = useState({
        visible: false, staffId: null, staffName: '',
        type: 'salary', amount: '', note: ''
    })
    const [submittingPayment, setSubmittingPayment] = useState(false)
    const [faqModal,          setFaqModal]          = useState(false)

    // ── VERİ ÇEKME — birebir aynı ────────────────────────────────────────────
    const fetchReports = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await api.get(`/admin/reports/general?start_date=${startDate}&end_date=${endDate}`)
            if (response.success) {
                setReportData(response)
            } else {
                setReportData(response)
            }
        } catch (err) {
            console.error('Rapor hatası:', err)
            setError('Rapor verileri alınamadı.')
        } finally {
            setLoading(false)
        }
    }, [startDate, endDate])

    useEffect(() => { fetchReports() }, [fetchReports])

    // ── HIZLI TARİH SEÇİMLERİ — birebir aynı ────────────────────────────────
    const handleQuickDate = (type) => {
        setActiveFilter(type)
        const today = new Date()
        const yyyy  = today.getFullYear()
        const mm    = String(today.getMonth() + 1).padStart(2, '0')
        const dd    = String(today.getDate()).padStart(2, '0')

        if (type === 'today') {
            const str = `${yyyy}-${mm}-${dd}`
            setStartDate(str)
            setEndDate(str)
        } else if (type === 'month') {
            const firstDay = `${yyyy}-${mm}-01`
            const lastDay  = new Date(yyyy, today.getMonth() + 1, 0).toISOString().split('T')[0]
            setStartDate(firstDay)
            setEndDate(lastDay)
        }
    }

    // ── openPaymentModal — birebir aynı ──────────────────────────────────────
    const openPaymentModal = (staff, type) => {
        let autoAmount = ''
        if (type === 'commission') {
            autoAmount = parseCurrencyToFloat(staff.metrics.commission_earned)
        }
        setPaymentModal({
            visible: true,
            staffId: staff.staff_id,
            staffName: staff.staff_name,
            type,
            amount: autoAmount,
            note: ''
        })
    }

    // ── handlePaymentSubmit — birebir aynı ───────────────────────────────────
    const handlePaymentSubmit = async () => {
        if (!paymentModal.amount || parseFloat(paymentModal.amount) <= 0) {
            alert('Lütfen geçerli bir tutar giriniz.')
            return
        }
        setSubmittingPayment(true)
        try {
            const isSalary     = paymentModal.type === 'salary'
            const categoryName = isSalary ? 'salary_expense' : 'commission_expense'
            const label        = isSalary ? 'Maaş Ödemesi' : 'Prim Ödemesi'
            const fullNote     = `${label} - ${paymentModal.note}`

            await api.post('/admin/payments/staff', {
                staff_id:     paymentModal.staffId,
                amount:       paymentModal.amount,
                payment_type: paymentModal.type,
                category:     categoryName,
                date:         new Date().toISOString().split('T')[0],
                note:         fullNote
            })

            setPaymentModal({ ...paymentModal, visible: false })
            fetchReports()
        } catch (err) {
            console.error('Ödeme hatası:', err)
            alert('Ödeme işlemi sırasında bir hata oluştu.')
        } finally {
            setSubmittingPayment(false)
        }
    }

    // ── DERIVED ───────────────────────────────────────────────────────────────
    const isSalaryPayment = paymentModal.type === 'salary'

    // ── RENDER ────────────────────────────────────────────────────────────────
    return (
        <div className="prim-page">
            <GlobalStyles />

            {/* ── 1. ÜST FİLTRE PANELİ ── */}
            <div className="prim-filter-card">
                <div>
                    <h3 className="prim-filter-title">
                        <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--prim-accent-l)', color: 'var(--prim-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <SpeedometerIcon size={20} />
                        </div>
                        Personel Hakediş Raporu
                    </h3>
                    <p className="prim-filter-sub">Maaş, prim ve finansal performans analizi</p>
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Quick filter chips */}
                    <div className="prim-chips-wrap">
                        <button
                            className={`prim-chip ${activeFilter === 'today' ? 'active' : ''}`}
                            onClick={() => handleQuickDate('today')}
                        >
                            <ClockIcon size={12} /> Bugün
                        </button>
                        <button
                            className={`prim-chip ${activeFilter === 'month' ? 'active' : ''}`}
                            onClick={() => handleQuickDate('month')}
                        >
                            <CalendarIcon size={12} /> Bu Ay
                        </button>
                    </div>

                    {/* Date range */}
                    <div className="prim-date-range">
                        <input
                            type="date"
                            className="prim-date-input"
                            value={startDate}
                            onChange={(e) => { setStartDate(e.target.value); setActiveFilter('custom') }}
                        />
                        <span className="prim-date-sep">→</span>
                        <input
                            type="date"
                            className="prim-date-input"
                            value={endDate}
                            onChange={(e) => { setEndDate(e.target.value); setActiveFilter('custom') }}
                        />
                        <button className="prim-search-btn" onClick={fetchReports} disabled={loading} title="Raporu Getir">
                            {loading ? <SpinnerIcon size={12} /> : <SearchIcon />}
                        </button>
                    </div>

                    {/* Help / Rehber */}
                    <button className="prim-help-btn" onClick={() => setFaqModal(true)} title="Rapor Rehberi">
                        <BookIcon size={16} />
                    </button>
                </div>
            </div>

            {/* ── 2. İÇERİK ── */}
            {loading ? (
                <>
                    <SummarySkeleton />
                    <div className="prim-staff-grid">
                        {[0, 1, 2].map(i => <StaffCardSkeleton key={i} />)}
                    </div>
                </>
            ) : error ? (
                <div className="prim-alert prim-alert-danger">
                    <WarnIcon /> {error}
                    <button className="prim-alert-close" onClick={() => setError(null)}><XIcon size={14} /></button>
                </div>
            ) : reportData ? (
                <>
                    {/* ── ÖZET KARTLAR ── */}
                    <div className="prim-summary-grid">
                        {/* Toplam Ciro */}
                        <div className="prim-hero-card">
                            <p className="prim-hero-label">Toplam Ciro</p>
                            <p className="prim-hero-value">{formatCurrency(reportData.shop_summary.total_revenue)}</p>
                            <p className="prim-hero-sub"><ChartLineIcon size={12} /> Seçili dönem toplam geliri</p>
                        </div>

                        {/* Hizmet & Ürün geliri */}
                        <div className="prim-rev-card">
                            <div className="prim-rev-row" style={{ marginBottom: 6 }}>
                <span className="prim-rev-label" style={{ color: 'var(--prim-info)' }}>
                  <CutIcon size={13} /> Hizmet Geliri
                </span>
                                <span className="prim-rev-value">{formatCurrency(reportData.shop_summary.total_service_revenue)}</span>
                            </div>
                            <ProgressBar
                                value={(reportData.shop_summary.total_service_revenue / (reportData.shop_summary.total_revenue || 1)) * 100}
                                color="var(--prim-info)" height={8}
                            />
                            <div style={{ height: 10 }} />
                            <div className="prim-rev-row" style={{ marginBottom: 6 }}>
                <span className="prim-rev-label" style={{ color: 'var(--prim-warn)' }}>
                  <BasketIcon size={13} /> Ürün Satışı
                </span>
                                <span className="prim-rev-value">{formatCurrency(reportData.shop_summary.total_product_revenue)}</span>
                            </div>
                            <ProgressBar
                                value={(reportData.shop_summary.total_product_revenue / (reportData.shop_summary.total_revenue || 1)) * 100}
                                color="var(--prim-warn)" height={8}
                            />
                        </div>

                        {/* Operasyon özeti */}
                        <div className="prim-ops-card">
                            <p className="prim-ops-label">Operasyon Özeti</p>
                            <div className="prim-ops-grid">
                                <div className="prim-ops-item">
                                    <p className="prim-ops-num">{reportData.shop_summary.total_appointments}</p>
                                    <p className="prim-ops-name">Randevu</p>
                                </div>
                                <div className="prim-ops-item">
                                    <p className="prim-ops-num">{reportData.shop_summary.total_products_sold}</p>
                                    <p className="prim-ops-name">Ürün</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── PERSONEL LİSTESİ ── */}
                    <div className="prim-section-divider">
                        <span className="prim-section-label">Personel Detayları</span>
                        <div className="prim-section-line" />
                    </div>

                    <div className="prim-staff-grid">
                        {reportData.staff_reports.map((staff, idx) => (
                            <StaffCard
                                key={staff.staff_id}
                                staff={staff}
                                onPayment={openPaymentModal}
                                animDelay={idx * 0.07}
                            />
                        ))}
                    </div>
                </>
            ) : null}

            {/* ── 3. ÖDEME MODALI — birebir iş mantığı aynı ── */}
            <Modal
                visible={paymentModal.visible}
                onClose={() => setPaymentModal({ ...paymentModal, visible: false })}
                title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: isSalaryPayment ? 'var(--prim-info)' : 'var(--prim-success)' }}>
            {isSalaryPayment ? <MoneyIcon size={16} /> : <CheckCircleIcon size={16} />}
                        {isSalaryPayment ? 'Maaş Ödemesi' : 'Prim Ödemesi'}
          </span>
                }
                footer={
                    <>
                        <button
                            className="prim-btn prim-btn-ghost"
                            onClick={() => setPaymentModal({ ...paymentModal, visible: false })}
                        >
                            İptal
                        </button>
                        <button
                            className={`prim-btn ${isSalaryPayment ? 'prim-btn-info' : 'prim-btn-success'}`}
                            onClick={handlePaymentSubmit}
                            disabled={submittingPayment}
                        >
                            {submittingPayment
                                ? <SpinnerIcon size={15} />
                                : <ArrowRightIcon />
                            }
                            Ödemeyi Onayla
                        </button>
                    </>
                }
            >
                <p className="prim-modal-staff-name">{paymentModal.staffName}</p>
                <p className="prim-modal-staff-sub">Ödeme işlemi yapılıyor</p>

                <div style={{ marginBottom: 16 }}>
                    <label className="prim-form-label">Tutar</label>
                    <div className="prim-input-group">
                        <span className="prim-input-prefix">₺</span>
                        <input
                            className="prim-input"
                            type="number"
                            placeholder="0.00"
                            value={paymentModal.amount}
                            onChange={(e) => setPaymentModal({ ...paymentModal, amount: e.target.value })}
                            autoFocus
                        />
                    </div>
                    {paymentModal.type === 'commission' && (
                        <p className="prim-hint-success">
                            <CheckCircleIcon size={12} /> Sistemdeki hakediş otomatik getirildi.
                        </p>
                    )}
                </div>

                <div>
                    <label className="prim-form-label">Açıklama</label>
                    <textarea
                        className="prim-textarea"
                        rows={2}
                        placeholder={isSalaryPayment ? 'Örn: Kasım Ayı Maaşı' : 'Örn: 1-15 Kasım Prim Ödemesi'}
                        value={paymentModal.note}
                        onChange={(e) => setPaymentModal({ ...paymentModal, note: e.target.value })}
                    />
                </div>
            </Modal>

            {/* ── 4. REHBER MODALI — birebir iş mantığı aynı ── */}
            <Modal
                visible={faqModal}
                onClose={() => setFaqModal(false)}
                size="prim-modal-lg"
                title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BookIcon size={16} /> Rapor Rehberi
          </span>
                }
                footer={
                    <button className="prim-btn prim-btn-primary" onClick={() => setFaqModal(false)}>
                        Anlaşıldı
                    </button>
                }
            >
                <div className="prim-accordion">
                    <AccordionItem title="Toplam Hakediş Nasıl Hesaplanır?" defaultOpen>
                        Yukarıdaki tarih aralığında personelin yaptığı tüm hizmet ve ürün satışları taranır.<br />
                        Personelin ayarlarına (Sabit Oran, Eşikli vb.) göre prim hesaplanır.<br />
                        Bu tutar, henüz ödenmemiş olan "Potansiyel Kazanç"tır.
                    </AccordionItem>
                    <AccordionItem title="Ciro Katkısı Nedir?">
                        Personelin kasaya soktuğu toplam paradır.<br />
                        <strong>Mavi renk</strong> Hizmet gelirlerini, <strong>Sarı renk</strong> Ürün satışlarını temsil eder.
                    </AccordionItem>
                    <AccordionItem title="Ödeme Yapınca Ne Olur?">
                        "Maaş Öde" veya "Prim Öde" butonuna bastığınızda kasadan para çıkışı (Gider) yapılır.<br />
                        Bu işlem personelin "Ödeme Geçmişi"ne işlenir ve kasanızın bakiyesinden düşülür.
                    </AccordionItem>
                </div>
            </Modal>
        </div>
    )
}

export default AdminStaffPrimSistemiListesi